import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { filter, take } from 'rxjs/operators';

interface ScriptConfig {
  source: string;
  integrity: string;
  id: string;
  tag: 'script' | 'link';
}
declare const shaka;
@Component({
  standalone: false,
  selector: 'campus-media-player',
  templateUrl: './media-player.component.html',
  styleUrls: ['./media-player.component.scss'],
})
export class MediaPlayerComponent implements AfterViewInit, OnInit {
  //#region shakaConfig
  private shakaPlayerConfig: ScriptConfig = {
    source: 'https://cdn.jsdelivr.net/npm/shaka-player@4.16.3/dist/shaka-player.compiled.min.js',
    integrity: 'sha384-f8sk772X10i6y4YL7IeLfEqdLvoFDT7dTbo1WETXhJ/td3idAyQOn9vzHv1hbMpv',
    id: 'ShakaPlayerScript',
    tag: 'script',
  };
  private shakaPlayerUiConfig: ScriptConfig = {
    source: 'https://cdn.jsdelivr.net/npm/shaka-player@4.16.3/dist/shaka-player.ui.js',
    id: 'ShakaPlayerUiScript',
    integrity: 'sha384-Pq0ysNlA1TzjgAQgP8ut5qm/haZsOkyqSa2TJRfZJUrVwrjNverXwIvs8SRiP96a',
    tag: 'script',
  };
  private shakaCssConfig: ScriptConfig = {
    source: 'https://cdn.jsdelivr.net/npm/shaka-player@4.16.3/dist/controls.css',
    id: 'ShakaPlayer',
    integrity: 'sha384-sywoxNeg9botKEG710gocqAxIUYYSKZ3DNS/pXrDO15SYDeWYHAxrKHlG3XuBbjK',
    tag: 'link',
  };
  //#endregion

  private player;

  @ViewChild('media', { static: true, read: ElementRef })
  media: ElementRef<HTMLVideoElement>;

  @Input()
  src: string;

  @Input() poster: string;

  @Input()
  controls = true;

  public ready$ = new BehaviorSubject(false);

  ngOnInit(): void {
    //get the css
    this.registerExternalScript(this.shakaCssConfig)
      .then(() => {
        //get the mainscript
        return this.registerExternalScript(this.shakaPlayerConfig);
      })
      .then(() => {
        //get the ui script
        return this.registerExternalScript(this.shakaPlayerUiConfig);
      })
      .finally(() => {
        //done
        this.ready$.next(true);
      });
  }

  ngAfterViewInit(): void {
    this.ready$
      .pipe(
        filter((ready) => ready),
        take(1)
      )
      .subscribe(() => {
        shaka.polyfill.installAll();
        this.initPlayer();
      });
  }

  private initPlayer() {
    this.player = new shaka.Player(this.media.nativeElement);
    this.player
      .load(this.src)
      .then(() => {})
      .catch(() => {});

    const ui = new shaka.ui.Overlay(this.player, this.media.nativeElement.parentElement, this.media.nativeElement);

    const config = {
      controlPanelElements: this.controls
        ? ['play_pause', 'mute', 'volume', 'time_and_duration', 'spacer', 'loop', 'picture_in_picture', 'fullscreen']
        : [],
    };

    ui.configure(config);
  }

  private registerExternalScript(scriptConfig: ScriptConfig): Promise<void> {
    if (document.getElementById(scriptConfig.id)) {
      const script = document.getElementById(scriptConfig.id);
      if (script.getAttribute('data-loaded') === 'true') {
        return Promise.resolve();
      } else {
        return new Promise((resolve, reject) => {
          script.addEventListener(
            'load',
            () => {
              resolve();
            },
            { once: true }
          );

          script.addEventListener(
            'error',
            () => {
              reject();
            },
            { once: true }
          );
        });
      }
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement(scriptConfig.tag);
      if (scriptConfig.tag === 'script') {
        script.type = 'text/javascript';
        script['src'] = scriptConfig.source;
        if (scriptConfig.integrity) {
          script.integrity = scriptConfig.integrity;
        }
        script['async'] = false;
      } else {
        script['rel'] = 'stylesheet';
        script['href'] = scriptConfig.source;
      }
      script.id = scriptConfig.id;
      script.crossOrigin = 'anonymous';
      script.onload = () => {
        script.setAttribute('data-loaded', 'true');
        resolve();
      };
      script.onerror = () => {
        reject();
      };
      document.getElementsByTagName('head')[0].appendChild(script);
    });
  }
}
